import axios from 'axios';
import { recordFailedRequest } from './diagnostics';

const api = axios.create({ baseURL: '/api', withCredentials: true });

// 2026-09-05: a példány EXPORTÁLT, hogy a `services/chat.js` is ezen menjen.
// Előzmény: a chat.js nyers `fetch()`-eket használt, amikre az alábbi
// `X-Company-Id` interceptor NEM futott le, így a backend a csendes
// `memberships[0]`-fallbackre esett és TÖBBCÉGES usernél a rossz cég adatát
// szolgálta ki (élő hiba, Gábor 2026-09-05-i Telegram-próbáján). Új API-hívás
// EZEN a kliensen menjen — nyers `fetch()` csak ott indokolt, ahol az axios
// nem elég (SSE-stream), és ott a fejlécet KÉZZEL kell feltenni.
export { api };

// ── Aktív cég (M1, 2026-09-04 — wiki/proposals/multi-tenant-company-switcher.md) ──
// A kliens EXPLICIT tárolja (localStorage — a UI-nak látnia kell, egy HttpOnly
// süti ezt szerkezetileg nem engedné) és minden kérésen `X-Company-Id`
// fejlécben küldi az aktív céget. A backend a fejlécet a user aktív
// membership-halmaza ellen validálja (hiányzó fejléc → determinisztikus
// memberships[0]-fallback; idegen cég → 403). A fejléc NEM titok és nem
// aláírt — nem is kell: a szerver úgyis minden kérésen validál. Az, hogy a
// fejléc NEM HttpOnly, egy XSS hatósugarát növelné N cégre — ezért a step-up
// jegy (v3) cég-kötött a backendben, cégváltás után az első pénzt mozgató
// művelet újra megerősítést kér.
export const COMPANY_HEADER = 'X-Company-Id';
export const ACTIVE_COMPANY_KEY = 'pf_active_company_id';
// A váltás utáni oldal egyszer leolvassa, toastot ad, és törli (Layout.js).
export const JUST_SWITCHED_KEY = 'pf_just_switched';

export function getStoredCompanyId() {
  try {
    const raw = localStorage.getItem(ACTIVE_COMPANY_KEY);
    const n = raw == null ? NaN : Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch { return null; }
}
export function setStoredCompanyId(id) {
  try { localStorage.setItem(ACTIVE_COMPANY_KEY, String(id)); } catch { /* privát mód */ }
}
export function clearStoredCompanyId() {
  try { localStorage.removeItem(ACTIVE_COMPANY_KEY); } catch { /* no-op */ }
}

api.interceptors.request.use((config) => {
  const id = getStoredCompanyId();
  if (id != null) config.headers[COMPANY_HEADER] = String(id);
  return config;
});

// 2026-07-31: Supabase Auth bekötés (wiki/SUPABASE_MIGRATION_PLAN.md 4. fejezet) -
// a cookie-alapú session miatt withCredentials kötelező, és 401-re egyszeri
// silent-refresh próbálkozás, mielőtt a userre /login-t kényszerítenénk.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { config, response } = error;

    // 2026-08-02 (admin F4): a sikertelen hívást felvesszük a hibajelentő
    // widget kontextusába. CSAK metódus + útvonal + státusz — kérés/válasz
    // törzs SOHA (számla-adatot tartalmazhatna).
    try {
      recordFailedRequest(config?.method, config?.url, response?.status);
    } catch { /* a diagnosztika sose törje el a hibakezelést */ }

    const onLoginPage = window.location.pathname === '/login';
    if (response?.status !== 401 || config?._retried || onLoginPage || config?.url === '/auth/refresh') {
      return Promise.reject(error);
    }
    config._retried = true;
    try {
      refreshing = refreshing || api.post('/auth/refresh');
      await refreshing;
      return api(config);
    } catch {
      window.location.href = '/login';
      return Promise.reject(error);
    } finally {
      refreshing = null;
    }
  }
);

export const authApi = {
  // `turnstileToken`: a 10. hibás próba után a szerver Turnstile-t kér (D-10,
  // components/LoginThrottle.js) — enélkül null, a szerver nem is várja.
  login:   (email, password, remember = false, turnstileToken = null) =>
    api.post('/auth/login', { email, password, remember, turnstile_token: turnstileToken })
      .then(r => r.data),
  logout:  () => api.post('/auth/logout').then(r => r.data),
  me:      () => api.get('/auth/me').then(r => r.data),
  // Friss hitelesítés az utalás-indításhoz (2026-08-04, F-1c).
  stepUpStatus: () => api.get('/auth/step-up').then(r => r.data),
  // ⚠ A jelszavas `POST /auth/step-up` 2026-09-24 óta KIVEZETVE (S3 / audit F4):
  // a backend 403 `passkey_kell`-t ad. Step-up csak a lenti passkey-ágon.
  // Passkey step-up ág (wiki/proposals/deferred-features.md §15): egy friss
  // WebAuthn-ceremónia ugyanúgy jegyet ad, mint a jelszavas ág.
  // ⚠ F6/A03 (2026-09-24): UTALÁSHOZ a `paymentIntent` kötelező — a konkrét kérések
  // (`{requests: [{endpoint, body}]}`, PONTOSAN azok a törzsek, amik utána mennek). A
  // backend ezekre ad egy egyszeri, 5 perces jegyet (`pf_paygrant` süti); a verify-nak
  // ilyenkor `payment: true` jár. Szándék nélkül a régi, NEM utalási step-up jegy jön.
  stepUpPasskeyOptions: (paymentIntent) => api.post('/auth/step-up/passkey/options',
    paymentIntent ? { payment_intent: paymentIntent } : undefined).then(r => r.data),
  stepUpPasskeyVerify:  (challengeId, credential, payment = false) =>
    api.post('/auth/step-up/passkey/verify',
      { challenge_id: challengeId, credential, payment }).then(r => r.data),
  // AAL2 step-up egy már regisztrált MFA-faktorral (2026-08-24, élő hiba: a
  // passkey regisztráció `insufficient_aal`-t ad, ha a fióknak van MFA-ja, pl.
  // az admin console TOTP-je). Nincs enrollment-felület, csak a meglévő
  // faktor HASZNÁLATA a jelenlegi session felemelésére.
  mfaFactors:  () => api.get('/auth/mfa/factors').then(r => r.data),
  mfaChallenge:(factorId) => api.post('/auth/mfa/challenge', { factor_id: factorId }).then(r => r.data),
  mfaVerify:   (factorId, challengeId, code) =>
    api.post('/auth/mfa/verify', { factor_id: factorId, challenge_id: challengeId, code }).then(r => r.data),
};

// Passkey (WebAuthn) belépés + Beállítások → Biztonság kezelőfelület.
// Two-step API proxy a backenden át (BFF-invariáns - lásd services/supabase_auth.py) -
// a böngésző csak a navigator.credentials ceremóniát futtatja
// (@simplewebauthn/browser startRegistration/startAuthentication).
// A GoTrue error_code-jai (raw/supabase-passkeys.md "Error codes" táblázata) -
// a backend a nyers kódot adja tovább `detail`-ben (lásd services/supabase_auth.py
// PasskeyError), ez a lookup fordítja magyarra. Ismeretlen kód -> generikus szöveg.
const PASSKEY_ERROR_HU = {
  passkey_disabled: 'A passkey-bejelentkezés jelenleg nincs engedélyezve.',
  too_many_passkeys: 'Elérted a fiókodhoz regisztrálható passkey-ek maximális számát.',
  webauthn_credential_exists: 'Ez az eszköz/kulcs már regisztrálva van.',
  webauthn_credential_not_found: 'Ez a passkey nincs regisztrálva ehhez a fiókhoz.',
  webauthn_challenge_not_found: 'A kérés lejárt, próbáld újra.',
  webauthn_challenge_expired: 'A kérés lejárt, próbáld újra.',
  webauthn_verification_failed: 'A passkey-ellenőrzés sikertelen.',
  email_not_confirmed: 'Az e-mail cím még nincs megerősítve.',
  phone_not_confirmed: 'A telefonszám még nincs megerősítve.',
  user_banned: 'Ez a fiók le van tiltva.',
  // MFA (AAL2 step-up, 2026-08-24) - nem passkey-specifikus GoTrue-kódok,
  // de ugyanazt a { error_code, msg } alakot adják, lásd PasskeyError.
  insufficient_aal: 'Ehhez a művelethez kétfaktoros megerősítés szükséges.',
  invalid_code: 'Hibás kód.',
  challenge_expired: 'A kérés lejárt, próbáld újra.',
};

export function passkeyErrorMessage(err, fallback = 'A passkey-művelet sikertelen.') {
  const code = err?.response?.data?.detail;
  // A backend saját (magyar, mondatnyi) üzenetei — pl. a passkey-kapu 403/409/429/503-ja
  // (2026-09-28, `core/passkey_guard.py`) — változatlanul mennek ki; a GoTrue-kódok fordítva.
  if (PASSKEY_ERROR_HU[code]) return PASSKEY_ERROR_HU[code];
  return (typeof code === 'string' && code.includes(' ')) ? code : fallback;
}

export const passkeyApi = {
  registrationOptions: () => api.post('/auth/passkeys/registration/options').then(r => r.data),
  // ⚠ R1 (2026-09-28): a verify a KAPU. Ha már van passkey, NE ezt hívd, hanem
  // `freshRequest('POST /auth/passkeys/registration/verify', {body})` + `requireFreshStepUp`
  // + `sendFresh` (a jóváhagyás a meglévő passkey-jel, erre az új kulcsra szól). Ez a wrapper
  // csak az ELSŐ passkeyhez való, az ellenőrzött e-mail-kódból kapott jeggyel (`firstCodeVerify`).
  registrationVerify:  (challengeId, credential, firstTicket) =>
    api.post('/auth/passkeys/registration/verify',
      { challenge_id: challengeId, credential, first_ticket: firstTicket }).then(r => r.data),
  // Az első passkeyhez: 6 jegyű kód a fiók e-mail-címére. Válasz: {sent, email (maszkolt), expires_in}.
  firstCode: () => api.post('/auth/passkeys/first-code').then(r => r.data),
  // A kód ellenőrzése a ceremónia ELŐTT → {ticket, expires_in} (5 perc, egyszeri).
  firstCodeVerify: (code) => api.post('/auth/passkeys/first-code/verify', { code }).then(r => r.data),
  authenticationOptions: () => api.post('/auth/passkeys/authentication/options').then(r => r.data),
  authenticationVerify:  (challengeId, credential, remember = false) =>
    api.post('/auth/passkeys/authentication/verify',
      { challenge_id: challengeId, credential, remember }).then(r => r.data),
  list:   () => api.get('/auth/passkeys').then(r => r.data),
  rename: (id, friendlyName) => api.patch(`/auth/passkeys/${id}`, { friendly_name: friendlyName }).then(r => r.data),
  // Törlés: friss passkey-jóváhagyás kell (R1) — `freshRequest('DELETE /auth/passkeys/{passkey_id}')`
  // + `sendFresh`, lásd `pages/misc.js` `SecurityPanel`. Közvetlen törlő wrapper szándékosan nincs.
};

// A backend 403 + `X-Step-Up-Required: 1` fejléccel jelzi, hogy a művelethez
// friss jelszó-megerősítés kell. SZÁNDÉKOSAN nem 401: azon a fenti interceptor
// néma /auth/refresh-t futtatna, majd /login-ra dobná a felhasználót az utalás
// közepén. Lásd backend/app/core/step_up.py.
export function isStepUpRequired(err) {
  return err?.response?.status === 403
      && String(err.response.headers?.['x-step-up-required'] || '') === '1';
}

// Halott wrapper-takarítás 2026-07-20 (review 7.1, hivatalos API-kkal validálva):
// a hívatlan wrapperek törölve; a mögöttes backend-endpointok (nav-test, nav-debug,
// backfill-*) MEGMARADTAK curl-diagnosztikának.
export const documentsApi = {
  // F0-8 / D53 (2026-09-29): a P&L „Forint-érték pótlása” gombja — a végpont ADMIN-kapus
  // (`core/admin_edit.require_admin_edit`), a gomb is csak ADMIN-nak látszik.
  backfillAmountHuf: () => api.post('/documents/backfill-amount-huf').then(r => r.data),
  // K-Q1 (2026-09-06): NAV↔PDF eltérés nyugtázása
  ackNavMismatch: (id) => api.post(`/documents/${id}/nav-mismatch/ack`).then(r => r.data),
  // Q2 (2026-09-06): tömeges státusz-beállítás a kijelölt bizonylatokra (védőhálókkal a backendben).
  bulkStatus: (ids, status, extra = {}) =>
    api.post('/documents/bulk-status', { ids, status, ...extra }).then(r => r.data),
  list:     (params) => api.get('/documents', { params }).then(r => r.data),
  get:      (id)     => api.get(`/documents/${id}`).then(r => r.data),
  // D6 (2026-09-06): tételsorok + ÁFA-bontás a generált számlaképhez
  lines:    (id)     => api.get(`/documents/${id}/lines`).then(r => r.data),
  update:   (id, d)  => api.patch(`/documents/${id}`, d).then(r => r.data),
  scan:     (fd)     => api.post('/documents/scan', fd).then(r => r.data),
  navSync:      (p)  => api.get('/documents/nav-sync', { params: p }).then(r => r.data),
  // B11+A13 (2026-09-06): NAV ↔ saját teljesség egy hónapra (bejövő + kimenő), csak összevetés
  navCompleteness: (month) => api.get('/documents/nav-completeness', { params: { month } }).then(r => r.data),
  partnerStats: (p)  => api.get('/documents/partner-stats', { params: p }).then(r => r.data),
  partnerBalance: (p)=> api.get('/documents/partner-balance', { params: p }).then(r => r.data),
  categoryStats: (p) => api.get('/documents/category-stats', { params: p }).then(r => r.data),
  revolutSync:         ()   => api.post('/documents/revolut-sync').then(r => r.data),
  // ⚠ A `paymentDraftsSync` MEGSZŰNT (2026-08-13) a `documents.payment_draft_id`
  //   markerrel együtt. Helyette: `paymentsApi.refreshPending()` — bankfüggetlen,
  //   és többszörös draft-leg egyezésnél nem tippel (nem állít téves PAID-et).
  attachPdf: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.patch(`/documents/${id}/pdf`, fd).then(r => r.data);
  },
  detachPdf: (id) => api.delete(`/documents/${id}/pdf`).then(r => r.data),
  reextract: (id) => api.post(`/documents/${id}/reextract`).then(r => r.data),
};

export const dashboardApi = {
  summary: () => api.get('/dashboard/summary').then(r => r.data),
  // KÜLÖN hívás (2026-08-04, F-3): az egyenlegek külső banki válaszra várnak
  // (mérve: 13 mp a Raiffeisen sandbox-timeoutja), a többi dashboard-adat 0,6 mp.
  // Így a nyitóképernyő nem a leglassabb bank sebességével fest.
  balances: () => api.get('/dashboard/balances').then(r => r.data),
  // Likviditás-előrejelzés vödrönként. Csak DB (gyors) — a nyitó egyenleg
  // szándékosan a `balances`-ról jön, hogy a chart oszlopai ne várjanak bankra.
  cashflowForecast: (params = {}) =>
    api.get('/dashboard/cashflow-forecast', { params }).then(r => r.data),
};

export const syncApi = {
  status: () => api.get('/sync/status').then(r => r.data),
};

export const statementsApi = {
  list:         (p)        => api.get('/statements', { params: p }).then(r => r.data),
  get:          (id)       => api.get(`/statements/${id}`).then(r => r.data),
  transactions: (id)       => api.get(`/statements/${id}/transactions`).then(r => r.data),
  upload:       (file)     => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/statements/upload', fd).then(r => r.data);
  },
  match:        (txId, docId) =>
    api.post(`/statements/transactions/${txId}/match/${docId}`).then(r => r.data),
  matchCandidates: (txId, q) =>
    api.get(`/statements/transactions/${txId}/match-candidates`,
            { params: q ? { q } : {} }).then(r => r.data),
  unmatch:      (txId, docId) =>
    api.delete(`/statements/transactions/${txId}/match/${docId}`).then(r => r.data),
  accounts:     ()         => api.get('/statements/accounts/list').then(r => r.data),
  // A2 (2026-09-06): Revolut tranzakciók a banki API-ból (strukturált, nem AI-olvasat)
  apiSyncRevolut: (days = 90) => api.post(`/statements/api-sync/revolut?days=${days}`).then(r => r.data),
  approve: (id) => api.post(`/statements/${id}/approve`).then(r => r.data),
  updateAccount: (id, data) => api.patch(`/statements/accounts/${id}`, data).then(r => r.data),
  // M8: tudatos „Bankszámla hozzáadása" (asset + számla-sor, kivonat-feltöltéses).
  createAccount: (data)     => api.post('/statements/accounts', data).then(r => r.data),
  deleteAccount: (id)       => api.delete(`/statements/accounts/${id}`).then(r => r.data),
  rematch:    (id) => api.post(`/statements/${id}/rematch`).then(r => r.data),
  rematchAll: ()   => api.post('/statements/rematch-all').then(r => r.data),
  allTransactions: (params) => api.get('/statements/transactions/all', { params }).then(r => r.data),
  toggleFee:  (txId, isBankFee, feeType=null) =>
    api.patch(`/statements/transactions/${txId}/fee`,
      { is_bank_fee: isBankFee, fee_type: feeType }).then(r => r.data),
  bankFeesSummary: (params) =>
    api.get('/statements/bank-fees/summary', { params }).then(r => r.data),
};

export const fxStatsApi = {
  analytics: (params) => api.get('/stats/fx', { params }).then(r => r.data),
};

// B4/F1 + B5 (2026-09-06): saját trendek — a bérlő saját múltja mint benchmark
export const ownTrendsApi = {
  priceSeries:      (params) => api.get('/stats/price-series', { params }).then(r => r.data),
  paymentBehaviour: (params) => api.get('/stats/payment-behaviour', { params }).then(r => r.data),
};

export const partnerAliasApi = {
  list:   ()         => api.get('/settings/partner-aliases').then(r => r.data),
  create: (d)        => api.post('/settings/partner-aliases', d).then(r => r.data),
  delete: (id)       => api.delete(`/settings/partner-aliases/${id}`).then(r => r.data),
};

export const employeesApi = {
  list:   ()        => api.get('/employees').then(r => r.data),
  create: (d)       => api.post('/employees', d).then(r => r.data),
  update: (id, d)   => api.patch(`/employees/${id}`, d).then(r => r.data),
  iban:   (account) => api.get('/employees/iban', { params: { account } }).then(r => r.data),

  // Dokumentumok (munkaszerződés, módosítás, felmondás stb.)
  parseDocument: (file, kind = 'MUNKASZERZODES') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    return api.post('/employees/parse-document', fd).then(r => r.data);
  },
  uploadDocument: (eid, file, kind = 'MUNKASZERZODES') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    return api.post(`/employees/${eid}/documents`, fd).then(r => r.data);
  },
  listDocuments: (eid)       => api.get(`/employees/${eid}/documents`).then(r => r.data),
  deleteDocument: (eid, did) => api.delete(`/employees/${eid}/documents/${did}`).then(r => r.data),
};

// payrollApi törölve (2026-07-20): egyetlen hívója a bekötetlen Payroll komponens
// volt (a payroll backend + e-mail automatizmus érintetlen).

export const emailApi = {
  // run param lehet: "2026-04" (string) VAGY {period} VAGY {date_from, date_to}
  run: (arg) => {
    const params = (typeof arg === 'string') ? { period: arg } : (arg || {});
    return api.post('/email/run', null, { params }).then(r => r.data);
  },
  debug: (params) => api.get('/email/debug', { params }).then(r => r.data),
};

// Hívatlan wrapperek törölve 2026-07-20 (review 7.1, a hivatalos revolut-openapi
// spec-kel validálva). A diagnosztikai backend-endpointok curl-ból élnek tovább.
export const revolutApi = {
  accounts:              ()        => api.get('/revolut/accounts').then(r => r.data),
  counterparties:        ()        => api.get('/revolut/counterparties').then(r => r.data),
  // ⚠ A `createCounterparty` wrapper 2026-09-24-én MEGSZŰNT: a counterparty a Revolut oldalán a
  //   célszámla (a pénz útja), ezért a `POST /revolut/counterparties` friss, kérésre kötött
  //   passkeyt kér → `freshRequest('POST /revolut/counterparties', {body})` + `sendFresh`
  //   (`Documents.js` PaymentModal „Partner felvétele"). Passkey nélküli wrapper ne legyen.
  createPayment:         (d)       => api.post('/revolut/payments', d).then(r => r.data),
  createPaymentDraft:    (d)       => api.post('/revolut/payment-drafts', d).then(r => r.data),
  envStatus:             ()        => api.get('/revolut/env-status').then(r => r.data),
};

/* ⚠⚠ Friss-passkey kör (2026-09-24, élő hiba): a pénz ÚTJÁT módosító műveletek
 * (munkavállalói/szállítói bankszámla, draft-védelem KI, banki hozzáférés) ugyanúgy friss,
 * EGYSZERI, a konkrét kérésre kötött passkey-jegyet kérnek, mint az utalás
 * (`backend/app/core/payment_step_up.py` `FRESH_ACTION_ENDPOINTS`). A lenyomat:
 * „METÓDUS /route-sablon" + path-paraméterek + query + törzs.
 *
 * A két helper azért van EGYÜTT, hogy a passkey-szándék és a tényleges kérés UGYANABBÓL a
 * leíróból készüljön — egy kézzel, két helyen összerakott törzs egy mezőnyi eltérésnél 403-at
 * adna (a backend fail-closed). Használat:
 *   const req = freshRequest('PATCH /employees/{eid}', { path: { eid: id }, body: d });
 *   if (!await requireFreshStepUp(why, [req])) return;
 *   await sendFresh(req);
 * A path- és query-értékek STRING-ként mennek (a szerver is stringet lát); az undefined/null
 * query-érték kimarad (ahogy az axios is kihagyja). */
export function freshRequest(endpoint, { path, query, body } = {}) {
  const str = (o) => Object.fromEntries(Object.entries(o || {})
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => [k, String(v)]));
  const req = { endpoint, path: str(path), query: str(query), body: body ?? null };
  // A törzsön egy JSON-kör: pontosan az, ami a hálózaton megy (az undefined mezők kiesnek).
  if (req.body !== null) req.body = JSON.parse(JSON.stringify(req.body));
  return req;
}

export function sendFresh(req) {
  const [method, template] = req.endpoint.split(' ');
  const url = template.replace(/\{(\w+)\}/g, (_, k) => encodeURIComponent(req.path[k]));
  return api.request({ method, url, params: req.query,
                       data: req.body === null ? undefined : req.body }).then(r => r.data);
}

export const paymentsApi = {
  capabilities: ()  => api.get('/payments/capabilities').then(r => r.data),
  // ⚠ `params` OPCIONÁLIS. Bizonylat nélkül (Beállítások → devizánkénti default
  // forrás) minden számla `eligible: true`-val jön — a hívó változatlan marad.
  // Bizonylattal (`{ document_id: '242,219' }`) a backend a BANK KÉPESSÉGEI szerint
  // dönt, és a nem alkalmas számlákon `requirement` jön („Nincs USD számlád").
  sources:      (params) => api.get('/payments/sources', { params }).then(r => r.data),
  // Raiffeisen-egyenlegek – KÜLÖN kérés, hogy a forrás-lista (és így az utalás-modal
  // megnyitása) ne várjon a bankra. Lásd a végpont docstringjét.
  balances:     ()  => api.get('/payments/balances').then(r => r.data),
  // Utalás ELŐTTI, olvasó ellenőrzés (2026-09-24): tételenként megbízható-e a számlaszám az
  // IBAN-kapu szerint (UGYANAZ a döntés, mint utaláskor), és tartósan megerősíthető-e.
  // Válasz: `{items: [{document_id, has_iban, iban_trusted, error, confirmable, has_partner,
  // reason, iban_masked}]}`. Nem ír, nincs rajta passkey.
  preflight:    (documentIds) =>
    api.post('/payments/preflight', { document_ids: documentIds }).then(r => r.data),
  // ⚠ F6/A03: a törzs lenyomata a passkey-jegy része — a hívó UGYANAZT az objektumot
  //   küldje, amit a `requirePaymentStepUp`-nak átadott (`PAY_ENDPOINTS`).
  pay:          (d) => api.post('/payments/pay', d).then(r => r.data),
  // ⚠ Köteg: a bank dönti el, hogy EGY kérés (Revolut draft) vagy N hívás
  //   (Raiffeisen, `maxItems: 1`) lesz belőle — a felület ezt NEM tudja, és nem is
  //   kell tudnia. A válasz `mode` mezője mondja meg, mi történt.
  payBatch:     (d) => api.post('/payments/pay-batch', d).then(r => r.data),
  // A lenyomatban szereplő végpont-kulcsok (a backend `PAYMENT_ENDPOINTS`-e, `/api` nélkül).
  PAY_ENDPOINTS: { pay: '/payments/pay', payBatch: '/payments/pay-batch' },
  // „Utalás ideje" legördülő: T / T-1 / T-2 / T-1M / T-2M a KONKRÉT dátumokkal.
  // A -M szabályok magyar BANKI naptáron számolnak (ünnep + áthelyezett munkarend),
  // ezért a dátumot a backend adja — a frontend nem tud munkanapot számolni.
  scheduleOptions: (dueDate) =>
    api.get('/payments/schedule-options', { params: dueDate ? { due_date: dueDate } : {} })
       .then(r => r.data),
  transactions: ()  => api.get('/payments/transactions').then(r => r.data),
  refreshTransaction: (source, id) =>
    api.post(`/payments/transactions/${source}/${id}/refresh`).then(r => r.data),
  // ⚠ A backend 60 s-os SZERVER-OLDALI féket alkalmaz (több fül / navigálás ne
  //   verje a bankot — a Raiffeisen 429-cel rate-limitel). `force: true` átlépi:
  //   ezt CSAK a kézi „Státuszok frissítése" gomb használja, aminek a jelentése
  //   „most kérdezd meg". A lap-megnyitáskori hívás SOHA ne forszírozzon.
  refreshPending: (force = false) =>
    api.post('/payments/transactions/refresh-pending', null,
             { params: force ? { force: true } : {} }).then(r => r.data),
  // 2026-08-25, élő hiba (#249): a `SUBMITTING` állapotban ragadt utalásokat
  // (pl. banki 5xx/timeout után, amikor a bank oldali sors nem ismert) SEM a
  // "Frissítés" gomb, SEM a lap-megnyitáskori automata poller nem tudja
  // lezárni - a `refreshTransaction`/`refreshPending` mögötti végpont
  // `local_state=SUBMITTED`-et követel meg. Ez a hívás a `POST
  // /payments/reconcile`-t futtatja, ami MINDKÉT bank felé lekérdezi a
  // ténylegesen SUBMITTING sorokat a bankon - eddig csak app-induláskor
  // futott le automatikusan, nincs hozzá se ütemező, se UI-gomb.
  reconcile: (olderThanMinutes) =>
    api.post('/payments/reconcile', null,
             { params: olderThanMinutes ? { older_than_minutes: olderThanMinutes } : {} })
       .then(r => r.data),
};

export const raiffeisenApi = {
  envStatus:      ()   => api.get('/raiffeisen/env-status').then(r => r.data),
  consents:       ()   => api.get('/raiffeisen/consents').then(r => r.data),
  createConsent:  (d)  => api.post('/raiffeisen/consents', d).then(r => r.data),
  refreshConsent: (id) => api.post(`/raiffeisen/consents/${id}/refresh`).then(r => r.data),
  balance:        (d)  => api.post('/raiffeisen/balance', d).then(r => r.data),
  payments:       ()   => api.get('/raiffeisen/payments').then(r => r.data),
  smsOtp:         (direktId) => api.post('/raiffeisen/sms-otp', { direkt_id: direktId }).then(r => r.data),
  // Kivonat-szinkron: a jóváhagyott számlák MÉG NEM behúzott kivonatait tölti le és
  // adja át az univerzális ingesternek. A `days` max 365 (a bank a 366. naptól
  // DATE_FROM_TOO_OLD-ot ad). AI-extrakciót futtat, ezért dedupál – lásd a backendet.
  syncStatements: (d)  => api.post('/raiffeisen/statements/sync', d || {}).then(r => r.data),
  // Consent-lejárat: a consent max 60 nap és NEM újítható; lejáratkor az utalás, az
  // egyenleg ÉS a kivonat-szinkron is megáll. Ezt a napi figyelmeztető modal kérdezi.
  consentExpiry:  ()   => api.get('/raiffeisen/consent-expiry').then(r => r.data),

  // ── Credential (Beállítások → Bankszámlák → API kapcsolat) ────────────────
  // ⚠ A GET SOHA nem ad vissza értéket (write-only titkok), csak `is_set`-et és
  //   időbélyeget. A mentés/törlés STEP-UP mögött van → 403 esetén a hívónak
  //   friss hitelesítést kell kérnie (a kódra illeszkedj, ne a szövegre).
  credentials:      (p) => api.get('/raiffeisen/credentials', { params: p || {} }).then(r => r.data),
  // ⚠ Az ÜRESEN hagyott mezőt NE küldd el: a backend az üres értéket
  //   „ne változtass"-ként kezeli, de a hálózaton se utazzon feleslegesen.
  saveCredentials:  (values) => api.put('/raiffeisen/credentials', values).then(r => r.data),
  clearCredentials: (fields, p) =>
    api.delete('/raiffeisen/credentials',
      { params: { fields: fields.join(','), ...(p || {}) } }).then(r => r.data),
  credentialHistory: (p) =>
    api.get('/raiffeisen/credentials/history', { params: p || {} }).then(r => r.data),
};

export const accountantApi = {
  send:             (d)      => api.post('/accountant/send', d).then(r => r.data),
  check:            (period) => api.get('/accountant/check', { params: { period } }).then(r => r.data),
  createWaiver:     (d)      => api.post('/accountant/waivers', d).then(r => r.data),
  deleteWaiver:     (id)     => api.delete(`/accountant/waivers/${id}`).then(r => r.data),
  recurringStats:   ()       => api.get('/accountant/recurring-stats').then(r => r.data),
  fetchBillingoPdf: (docId)  => api.post(`/accountant/fetch-billingo-pdf/${docId}`).then(r => r.data),
  bulkUpload: (period, file, signal, opts = {}) => {
    const fd = new FormData(); fd.append('file', file);
    const params = { period };
    if (opts.forceCreate) params.force_create = true;
    return api.post('/accountant/bulk-upload', fd, { params, signal }).then(r => r.data);
  },
  sendPackage:      (d)      => api.post('/accountant/send-package', d).then(r => r.data),
  history:          ()       => api.get('/accountant/history').then(r => r.data),
};

export const partnersApi = {
  list:   ()      => api.get('/partners').then(r => r.data),
  // B3 (2026-09-16): egyedi partner — a mélylinkelt panel (/partners/:id) ebből él,
  // hogy F5 után is legyen friss adata (a lista-elemből feltöltve üres maradna).
  get:    (id)    => api.get(`/partners/${id}`).then(r => r.data),
  create: (d)     => api.post('/partners', d).then(r => r.data),
  update: (id, d) => api.patch(`/partners/${id}`, d).then(r => r.data),
  // S1 / audit F1 (2026-09-24): a bizonylaton ÉSZLELT szállítói IBAN tartós megerősítése.
  // Passkey step-up-os (403 + X-Step-Up-Required) és utalási jogot kér (403
  // `nincs_utalasi_jog`); a hívó ELŐBB `requireStepUp()`-ot fut. A megerősítés MINDIG egy
  // konkrét (bizonylat, IBAN) párra szól — globális „mind megerősítve" jelző nincs.
  confirmBankAccount: (documentId, iban) =>
    api.post('/partners/bank-accounts/confirm', { document_id: documentId, iban }).then(r => r.data),
};

export const portalFetchApi = {
  providers: (month) => api.get('/portal-fetch/providers', { params: { month } }).then(r => r.data),
};

// Cégadatok — a `companies` tábla. A korábbi `settings`-kulcsok
// (company_name / company_tax_reference / accountant_email) MEGSZŰNTEK: ugyanaz az
// adat két helyen élt, és szét is csúszott (lásd backend/app/api/company.py).
export const companyApi = {
  get:   ()      => api.get('/company').then(r => r.data),
  patch: (data)  => api.patch('/company', data).then(r => r.data),
};

export const settingsApi = {
  get:        ()           => api.get('/settings').then(r => r.data),
  set:        (key, value) => api.post('/settings', null, { params: { key, value } }).then(r => r.data),
  uploadLogo: (file)       => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/settings/logo', fd).then(r => r.data);
  },
  deleteLogo: ()           => api.delete('/settings/logo').then(r => r.data),
};

export const categoriesApi = {
  list:        ()       => api.get('/settings/categories').then(r => r.data),
  save:        (cats)   => api.put('/settings/categories', cats).then(r => r.data),
  listRules:   ()       => api.get('/settings/category-rules').then(r => r.data),
  createRule:  (d)      => api.post('/settings/category-rules', d).then(r => r.data),
  updateRule:  (id, d)  => api.patch(`/settings/category-rules/${id}`, d).then(r => r.data),
  deleteRule:  (id)     => api.delete(`/settings/category-rules/${id}`).then(r => r.data),
};

export const projectsApi = {
  list:               ()           => api.get('/projects').then(r => r.data),
  create:             (d)          => api.post('/projects', d).then(r => r.data),
  update:             (id, d)      => api.patch(`/projects/${id}`, d).then(r => r.data),
  delete:             (id)         => api.delete(`/projects/${id}`).then(r => r.data),
  // F0-4/F0-8: `{period_from, period_to}` = ÉÉÉÉ-HH (teljesítési hónap); érvénytelenre 400.
  pnl:                ({ period_from, period_to }) => api.get('/projects/pnl', { params: { period_from, period_to } }).then(r => r.data),
  documentAllocations:(docId)      => api.get(`/documents/${docId}/allocations`).then(r => r.data),
  createAllocation:   (docId, d)   => api.post(`/documents/${docId}/allocations`, d).then(r => r.data),
  deleteAllocation:   (allocId)    => api.delete(`/projects/allocations/${allocId}`).then(r => r.data),
  suggestAllocations: (docId)      => api.get(`/documents/${docId}/allocations/suggest`).then(r => r.data),
};

export const billingoApi = {
  // M7: `assetId` nélkül a backend MINDEN aktív Billingo-fiókot végigjár.
  sync: (date_from, date_to, assetId) =>
    api.get('/billingo/sync', { params: { date_from, date_to, ...(assetId ? { asset_id: assetId } : {}) } }).then(r => r.data),
};

/* NAV adófolyószámla (M2M Adózó API).
 * ⚠ EZ NEM az Online Számla — az a documentsApi.navSync (bejövő számlák).
 * A credential-mezők WRITE-ONLY: a GET csak azt adja vissza, MELYIK van
 * beállítva, értéket soha. A PUT/DELETE step-up-os (403 + X-Step-Up-Required),
 * ezért a hívás ELŐTT requireStepUp()-ot kell futtatni. */
// M5 (2026-09-05): asset-regiszter — a cég külső kapcsolatai őszinte állapottal
// (`wiki/proposals/asset-registry.md`). A `status` CSAK a `/test` elmentett
// eredményétől vált `active`-ra, a mentés 200-ától SOHA. Létrehozás/rotálás/törlés
// step-up-os (a hívó ELŐBB `requireStepUp()`-ot fut).
export const companyAssetsApi = {
  list:        (assetType) => api.get('/company-assets', { params: assetType ? { asset_type: assetType } : {} }).then(r => r.data),
  create:      (body)      => api.post('/company-assets', body).then(r => r.data),
  test:        (id)        => api.post(`/company-assets/${id}/test`).then(r => r.data),
  rotate:      (id, body)  => api.post(`/company-assets/${id}/credentials`, body).then(r => r.data),
  // Érték NÉLKÜL: is_set + időbélyeg + verzió-címke + history_count (M7).
  credentials: (id)        => api.get(`/company-assets/${id}/credentials`).then(r => r.data),
  credentialHistory: (id)  => api.get(`/company-assets/${id}/credentials/history`).then(r => r.data),
  // M8: kapcsolat-szintű beállítás (pl. Revolut use_drafts) — a backend a provider
  // képessége szerint allowlistel, `true→false` irányban step-upot kér (403).
  patchSettings: (id, settings) => api.patch(`/company-assets/${id}/settings`, { settings }).then(r => r.data),
  disable:     (id)        => api.post(`/company-assets/${id}/disable`).then(r => r.data),
  enable:      (id)        => api.post(`/company-assets/${id}/enable`).then(r => r.data),
  remove:      (id, withCredentials = false) =>
    api.delete(`/company-assets/${id}`, { params: { with_credentials: withCredentials } }).then(r => r.data),
};

export const navUpoApi = {
  state:        ()      => api.get('/nav-upo/state').then(r => r.data),
  /* A NAV ÜPO cégenkénti ki/be kapcsolója (2026-09-27) — csak ADMIN. Kikapcsolva
   * SEMMILYEN NAV ÜPO hívás nem megy; alapértelmezés KI. */
  setEnabled:   (enabled) => api.post('/nav-upo/enabled', null, { params: { enabled } }).then(r => r.data),
  sync:         (p)     => api.post('/nav-upo/sync', null, { params: p }).then(r => r.data),
  probe:        (variant) => api.post('/nav-upo/probe', null, { params: variant ? { variant } : {} }).then(r => r.data),
  credentials:  (environment) => api.get('/nav-upo/credentials', { params: environment ? { environment } : {} }).then(r => r.data),
  saveCredentials: (values, environment) =>
    api.put('/nav-upo/credentials', values, { params: environment ? { environment } : {} }).then(r => r.data),
  clearCredentials: (fields, environment) =>
    api.delete('/nav-upo/credentials', { params: { fields: fields.join(','), ...(environment ? { environment } : {}) } }).then(r => r.data),
  /* A végfelhasználói regisztráció aktiválása: nonce-beváltás + aktiválás.
   * ⏱ A nonce EGYSZER használatos és 72 órás. Step-up-os végpont, tehát a hívás
   * ELŐTT requireStepUp() kell. */
  activate:     (variant) => api.post('/nav-upo/activate', null,
                             { params: variant ? { variant } : {} }).then(r => r.data),
  // ⚠ `setEnvironment` MEGSZŰNT (2026-09-04, M3): a NAV ÜPO környezet a cég-szintű
  // `is_sandbox` flag, amit csak a PhenoFin-Admin konzol állít. A `state` válasz
  // `environment` mezője továbbra is megmondja, melyikben vagyunk.
  /* A lekérdezett adóalany felülbírálása — CSAK teszt-környezetben érvényesül.
   * A NAV teszt-környezete anonimizált teszt-adózókkal dolgozik; ezzel váltható,
   * melyiket kérdezzük (üres = vissza a cég adószámára). */
  setAdoalanyOverride: (value) => api.post('/nav-upo/adoalany-override', null,
                             { params: { value } }).then(r => r.data),
  /* A NAV sandbox teszt-adózói (dropdown az override-hoz). Éles környezetben a
   * végpont `applicable: false`-ot ad — ott az override nem érvényesül. */
  testTaxpayers: ()     => api.get('/nav-upo/test-taxpayers').then(r => r.data),
  setVariant:   (variant) => api.post('/nav-upo/signature-variant', null, { params: { variant } }).then(r => r.data),
  /* Adófolyószámla-NÉZET (Információk → NAV adófolyószámla). Nem összevetés: a
   * NAV-tól kapott adat MEGTEKINTÉSE, szűrés/lapozás nélküli fej + tétel-lista. */
  view:         ()      => api.get('/nav-upo/view').then(r => r.data),
  /* Compliance (9. lépés): hiányzó bevallás + köztartozás + KOMA. OLVASÓ
   * művelet a NAV felé, bizonylatot NEM ír. */
  compliance:     ()    => api.get('/nav-upo/compliance').then(r => r.data),
  complianceSync: (endpoint) => api.post('/nav-upo/compliance/sync', null,
                             { params: endpoint ? { endpoint } : {} }).then(r => r.data),
  /* auto-PAID (9. lépés) — ⚠ EZ ÍR: bizonylatot állít PAID-re. A `dry_run`
   * alapból TRUE. A kár iránya itt ELMARADT utalás (a projekciónál dupla
   * utalás), ezért csak `auto_safe` tétel írható: a RÉSZFIZETÉS nem PAID. */
  settlePlan:   ()      => api.post('/nav-upo/settle', null,
                             { params: { dry_run: true } }).then(r => r.data),
  settleApply:  (itemKeys) => api.post('/nav-upo/settle',
                             { item_keys: itemKeys },
                             { params: { dry_run: false } }).then(r => r.data),
  items:        (p)     => api.get('/nav-upo/items', { params: p }).then(r => r.data),
  variants:     ()      => api.get('/nav-upo/signature-variants').then(r => r.data),
  callLog:      (p)     => api.get('/nav-upo/call-log', { params: p }).then(r => r.data),
  // Összevetés (3. fázis) — bizonylatot NEM ír, csak megmutatja az eltéréseket.
  reconcile:    (p)     => api.post('/nav-upo/reconcile', null, { params: p }).then(r => r.data),
  /* Projekció (4. fázis) — ⚠ EZ ÍR: bizonylat keletkezhet, ami megjelenik az
   * Utalandó listán. A `dry_run` alapból TRUE; írni csak explicit
   * `dry_run: false` + a jóváhagyott `item_keys` átadásával lehet. */
  projectPlan:  (p)     => api.post('/nav-upo/project', null,
                             { params: { ...p, dry_run: true } }).then(r => r.data),
  projectApply: (itemKeys, p) => api.post('/nav-upo/project',
                             { item_keys: itemKeys },
                             { params: { ...p, dry_run: false } }).then(r => r.data),
  // A hívásnapló letöltése — NAV 6.D/3. működési elvárás.
  callLogCsvUrl: (onlyErrors) =>
    `/api/nav-upo/call-log.csv${onlyErrors ? '?only_errors=true' : ''}`,
};

export const previewUrl = (docId) => `/api/documents/${docId}/preview`;
// 2026-09-06: a PDF a SAJÁT bejelentkezésen át (backend-proxy), NEM a SharePoint webUrl —
// a közvetlen link csak a PhenoGyde-tenant Microsoft-fiókjainak nyílt meg.
export const documentPdfUrl = (docId) => `/api/documents/${docId}/pdf`;
export const statementPdfUrl = (stmtId) => `/api/statements/${stmtId}/pdf`;

// D7 (2026-09-06): adatexport — a bérlő saját adata CSV/ZIP alakban (ADMIN jog).
// ⚠ Blobként, az axios-példányon: egy sima <a href> letöltés NEM vinné az
// `X-Company-Id` fejlécet → többcéges usernél a rossz cég adatát adná ki.
export const exportApi = {
  manifest: () => api.get('/export/manifest').then(r => r.data),
  download: async (dataset) => {
    const url = dataset === 'all' ? '/export/all.zip' : `/export/${dataset}.csv`;
    const r = await api.get(url, { responseType: 'blob' });
    const cd = r.headers?.['content-disposition'] || '';
    const m = /filename="([^"]+)"/.exec(cd);
    const name = m ? m[1] : (dataset === 'all' ? 'puo-adatexport.zip' : `puo-${dataset}.csv`);
    const href = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = href; a.download = name; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(href);
    return name;
  },
};

// 2026-08-02 (admin F4): felhasználói hibajelentés / javaslat.
export const feedbackApi = {
  create: (payload) => api.post('/feedback', payload).then(r => r.data),
  mine:   ()        => api.get('/feedback/mine').then(r => r.data),
};
